import { nanoid } from 'nanoid';
import type { ValueOf } from 'type-fest';

import { notify } from 'utils/reduxUtils';

import type {
  SuggestionAnnotationCommandNoId,
  SuggestionAnnotationStateMap,
} from '../annotationCommands';
import { SUGGESTION_ANNOTATION_COMMAND_ORIGINS } from '../annotationCommands/consts';
import type { ReadOnlyAnnotation } from './types';

export const types = {
  APPLY_REDACTIONS_ERROR: 'common/pdfViewer/APPLY_REDACTIONS_ERROR',
  APPLY_REDACTIONS_REQUEST: 'common/pdfViewer/APPLY_REDACTIONS_REQUEST',
  APPLY_REDACTIONS_SUCCESS: 'common/pdfViewer/APPLY_REDACTIONS_SUCCESS',
  CLOSE_APPLY_REDACTIONS_MODAL: 'common/pdfViewer/CLOSE_APPLY_REDACTIONS_MODAL',
  OPEN_APPLY_REDACTIONS_MODAL: 'common/pdfViewer/OPEN_APPLY_REDACTIONS_MODAL',
  REDACT_FILE: 'common/pdfViewer/REDACT_FILE',
  JUMP_TO_ANNOTATION_REQUEST: 'common/pdfViewer/JUMP_TO_ANNOTATION_REQUEST',
  JUMP_TO_ANNOTATION_SUCCESS: 'common/pdfViewer/JUMP_TO_ANNOTATION_SUCCESS',
  MANUALLY_SCALE_MEASUREMENT_TOOLS_SUCCESS:
    'common/pdfViewer/MANUALLY_SCALE_MEASUREMENT_TOOLS_SUCCESS',
  PDF_SEARCH_REQUEST: 'common/pdfViewer/PDF_SEARCH_REQUEST',
  PDF_SEARCH_SUCCESS: 'common/pdfViewer/PDF_SEARCH_SUCCESS',
  PDF_VIEWER_RESET: 'common/pdfViewer/PDF_VIEWER_RESET',
  RENDER_READ_ONLY_ANNOTATIONS_REQUEST:
    'common/pdfViewer/RENDER_READ_ONLY_ANNOTATIONS_REQUEST',
  RENDER_READ_ONLY_ANNOTATIONS_SUCCESS:
    'common/pdfViewer/RENDER_READ_ONLY_ANNOTATIONS_SUCCESS',
  REMOVE_ANNOTATIONS_REQUEST: 'common/pdfViewer/REMOVE_ANNOTATIONS_REQUEST',
  REMOVE_ANNOTATIONS_SUCCESS: 'common/pdfViewer/REMOVE_ANNOTATIONS_SUCCESS',
  RERENDER_ALL_PAGE_SCALES: 'common/pdfViewer/RERENDER_ALL_PAGE_SCALES',
  RERENDER_ALL_MEASUREMENT_ANNOTATIONS_SUCCESS:
    'common/pdfViewer/RERENDER_ALL_MEASUREMENT_ANNOTATIONS_SUCCESS',
  SUGGESTION_ANNOTATION_COMMAND_QUEUE:
    'common/pdfViewer/SUGGESTION_ANNOTATION_COMMAND_QUEUE',
  SUGGESTION_ANNOTATION_COMMAND_SUCCESS:
    'common/pdfViewer/SUGGESTION_ANNOTATION_COMMAND_SUCCESS',
  SUGGESTION_ANNOTATION_TOGGLE_SHOW_EXTRACTIONS:
    'common/pdfViewer/SUGGESTION_ANNOTATION_TOGGLE_SHOW_EXTRACTIONS',
  UPDATE_CURRENT_PAGE_NUMBER: 'common/pdfViewer/UPDATE_CURRENT_PAGE_NUMBER',
  UPDATE_PAGE_SCALE_ERROR: 'common/pdfViewer/UPDATE_PAGE_SCALE_ERROR',
  UPDATE_PAGE_SCALE_REQUEST: 'common/pdfViewer/UPDATE_PAGE_SCALE_REQUEST',
  UPDATE_PAGE_SCALE_SUCCESS: 'common/pdfViewer/UPDATE_PAGE_SCALE_SUCCESS',
  VIEWER_ANNOTATIONS_ADD: 'common/pdfViewer/VIEWER_ANNOTATIONS_ADD',
  VIEWER_ANNOTATIONS_DELETE: 'common/pdfViewer/VIEWER_ANNOTATIONS_DELETE',
  VIEWER_ANNOTATIONS_MODIFY: 'common/pdfViewer/VIEWER_ANNOTATIONS_MODIFY',
  VIEWER_ANNOTATIONS_REMOVE_ALL:
    'common/pdfViewer/VIEWER_ANNOTATIONS_REMOVE_ALL',
  VIEWER_MEASUREMENTS_ADD: 'common/pdfViewer/VIEWER_MEASUREMENTS_ADD',
  VIEWER_MEASUREMENTS_DELETE: 'common/pdfViewer/VIEWER_MEASUREMENTS_DELETE',
  VIEWER_MEASUREMENTS_MODIFY: 'common/pdfViewer/VIEWER_MEASUREMENTS_MODIFY',
  VIEWER_MEASUREMENTS_REMOVE_ALL:
    'common/pdfViewer/VIEWER_MEASUREMENTS_REMOVE_ALL',
} as const;

export const applyRedactions = {
  error: notify(
    (error) => ({
      type: types.APPLY_REDACTIONS_ERROR,
      payload: { error },
    }),
    'Failed to apply redactions',
  ),
  request: ({ fileName }: { fileName: string }) => ({
    type: types.APPLY_REDACTIONS_REQUEST,
    payload: { fileName },
  }),
  success: () => ({
    type: types.APPLY_REDACTIONS_SUCCESS,
  }),
  redactFile: ({ redactedBlob }: { redactedBlob: Blob }) => ({
    type: types.REDACT_FILE,
    payload: { redactedBlob },
  }),
};

export const applyRedactionsModal = {
  close: () => ({
    type: types.CLOSE_APPLY_REDACTIONS_MODAL,
  }),
  open: () => ({
    type: types.OPEN_APPLY_REDACTIONS_MODAL,
  }),
};

export const jumpToAnnotation = {
  request: ({ annotationId }: { annotationId: string }) => ({
    type: types.JUMP_TO_ANNOTATION_REQUEST,
    payload: { annotationId },
  }),
  success: () => ({
    type: types.JUMP_TO_ANNOTATION_SUCCESS,
  }),
};

export const pdfSearch = {
  request: ({ query }: { query: string }) => ({
    type: types.PDF_SEARCH_REQUEST,
    payload: { query },
  }),
  success: () => ({
    type: types.PDF_SEARCH_SUCCESS,
  }),
};

export const pdfViewer = {
  reset: () => ({
    type: types.PDF_VIEWER_RESET,
  }),
};

export const renderReadOnlyAnnotations = {
  request: ({
    readOnlyAnnotations,
  }: {
    readOnlyAnnotations: ReadOnlyAnnotation[];
  }) => ({
    type: types.RENDER_READ_ONLY_ANNOTATIONS_REQUEST,
    payload: { readOnlyAnnotations },
  }),
  success: ({ annotationIds }: { annotationIds: string[] }) => ({
    type: types.RENDER_READ_ONLY_ANNOTATIONS_SUCCESS,
    payload: { annotationIds },
  }),
};

export const removeAnnotations = {
  request: ({ annotationIds }: { annotationIds: string[] }) => ({
    type: types.REMOVE_ANNOTATIONS_REQUEST,
    payload: { annotationIds },
  }),
  success: ({ annotationIds }: { annotationIds: string[] }) => ({
    type: types.REMOVE_ANNOTATIONS_SUCCESS,
    payload: { annotationIds },
  }),
};

export const rerenderPageScales = {
  request: () => ({
    type: types.RERENDER_ALL_PAGE_SCALES,
  }),
  annotationsSuccess: () => ({
    type: types.RERENDER_ALL_MEASUREMENT_ANNOTATIONS_SUCCESS,
  }),
  toolsSuccess: () => ({
    type: types.MANUALLY_SCALE_MEASUREMENT_TOOLS_SUCCESS,
  }),
};

export const updatePageScale = {
  error: (error: unknown) => ({
    type: types.UPDATE_PAGE_SCALE_ERROR,
    payload: { error },
  }),
  request: ({
    annotationChangeEvent,
    pageNumber,
    scale,
  }: {
    annotationChangeEvent: string;
    pageNumber: number;
    scale: unknown;
  }) => ({
    type: types.UPDATE_PAGE_SCALE_REQUEST,
    payload: { annotationChangeEvent, pageNumber, scale },
  }),
  success: () => ({
    type: types.UPDATE_PAGE_SCALE_SUCCESS,
  }),
};

export const updateCurrentPageNumber = ({
  pageNumber,
}: {
  pageNumber: number;
}) => ({
  type: types.UPDATE_CURRENT_PAGE_NUMBER,
  payload: { pageNumber },
});

export const viewerAnnotations = {
  add: ({
    id,
    zoom,
    xfdfString,
  }: {
    id: string;
    zoom: unknown;
    xfdfString: string;
  }) => ({
    type: types.VIEWER_ANNOTATIONS_ADD,
    payload: { id, zoom, xfdfString },
  }),
  delete: ({ ids }: { ids: string[] }) => ({
    type: types.VIEWER_ANNOTATIONS_DELETE,
    payload: { ids },
  }),
  modify: ({ id, xfdfString }: { id: string; xfdfString: string }) => ({
    type: types.VIEWER_ANNOTATIONS_MODIFY,
    payload: { id, xfdfString },
  }),
  removeAll: () => ({
    type: types.VIEWER_ANNOTATIONS_REMOVE_ALL,
  }),
};

export const viewerMeasurements = {
  add: ({
    id,
    zoom,
    xfdfString,
  }: {
    id: string;
    zoom: unknown;
    xfdfString: string;
  }) => ({
    type: types.VIEWER_MEASUREMENTS_ADD,
    payload: { id, zoom, xfdfString },
  }),
  delete: ({ ids }: { ids: string[] }) => ({
    type: types.VIEWER_MEASUREMENTS_DELETE,
    payload: { ids },
  }),
  modify: ({ id, xfdfString }: { id: string; xfdfString: string }) => ({
    type: types.VIEWER_MEASUREMENTS_MODIFY,
    payload: { id, xfdfString },
  }),
  removeAll: () => ({
    type: types.VIEWER_MEASUREMENTS_REMOVE_ALL,
  }),
};

export const suggestionAnnotations = {
  queueCommands: ({
    commands,
  }: {
    commands: SuggestionAnnotationCommandNoId[];
  }) => ({
    type: types.SUGGESTION_ANNOTATION_COMMAND_QUEUE,
    payload: {
      commands: commands.map((cmd) => ({ ...cmd, id: nanoid() })),
    },
  }),
  acknowledgeCommands: ({
    commandIds,
    nextStateMap = {},
    countRedraws = 0,
    duration = 0,
    focusedAnnotationOrigin,
  }: {
    commandIds: string[];
    nextStateMap?: SuggestionAnnotationStateMap;
    countRedraws?: number;
    duration?: number;
    focusedAnnotationOrigin?: ValueOf<
      typeof SUGGESTION_ANNOTATION_COMMAND_ORIGINS
    > | null;
  }) => ({
    type: types.SUGGESTION_ANNOTATION_COMMAND_SUCCESS,
    payload: {
      commandIds,
      nextStateMap,
      countRedraws,
      duration,
      ...(focusedAnnotationOrigin !== undefined && { focusedAnnotationOrigin }),
    },
  }),
  toggleShowExtractions: () => ({
    type: types.SUGGESTION_ANNOTATION_TOGGLE_SHOW_EXTRACTIONS,
  }),
};
